/* ==========================================================================
   WISH LIST
   Saved products live in localStorage under "synergy:wishlist" (an array of
   product handles). Product details are fetched from the storefront's own
   /products/<handle>.js when the drawer opens, so price, availability and
   variants are never stale.

   Loaded site-wide, so the header count and the hearts on product cards stay
   in step on every page.
   ========================================================================== */

(function () {
  'use strict';

  const KEY = 'synergy:wishlist';

  const money = (cents) => {
    const fmt = window.themeVariables?.settings?.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && fmt) {
      return window.Shopify.formatMoney(cents, fmt);
    }
    return '$' + (cents / 100).toFixed(2);
  };

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function read() {
    try {
      return JSON.parse(window.localStorage.getItem(KEY)) || [];
    } catch (e) {
      return [];
    }
  }

  function write(items, options) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(items));
    } catch (e) {
      /* private browsing - the list simply will not persist */
    }
    if (!options || options.push !== false) {
      // Defined further down; hoisted function declaration, safe to call here.
      pushToAccount(items);
    }
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: items } }));
  }

  const has = (handle) => read().indexOf(handle) !== -1;

  function toggle(handle) {
    const items = read();
    const i = items.indexOf(handle);
    if (i === -1) items.push(handle);
    else items.splice(i, 1);
    write(items);
    return i === -1;
  }

  function remove(handle) {
    write(read().filter((h) => h !== handle));
  }

  /* ---------- Optional sync to the customer account ----------
     Enabled by theme setting; see tools/wishlist-app. Every call fails soft:
     if the proxy is missing or erroring the list still works from
     localStorage, so the storefront can never be broken by the service. */
  const sync = window.SynergyWishlistConfig || {};
  const syncing = () => Boolean(sync.enabled && sync.signedIn);
  const proxy = () => (sync.proxyPath || '/apps/wishlist');

  async function pullFromAccount() {
    if (!syncing()) return;
    try {
      const res = await fetch(proxy(), { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data.handles)) return;

      // Merge, so anything saved while signed out survives signing in.
      const merged = [...new Set([...read(), ...data.handles])];
      const local = read();
      const changed = merged.length !== local.length || merged.some((h, i) => h !== local[i]);
      if (changed) write(merged, { push: false });
      if (merged.length !== data.handles.length) pushToAccount(merged);
    } catch (e) {
      /* offline or proxy not deployed - localStorage still holds the list */
    }
  }

  async function pushToAccount(handles) {
    if (!syncing()) return;
    try {
      await fetch(proxy(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ handles: handles }),
      });
    } catch (e) {
      /* same - the local list remains the source of truth */
    }
  }

  window.SynergyWishlist = { read: read, write: write, has: has, toggle: toggle, remove: remove };
  document.addEventListener('DOMContentLoaded', pullFromAccount);

  /* ---------- Heart on a product card ---------- */
  if (!window.customElements.get('synergy-wish-button')) {
    class SynergyWishButton extends HTMLElement {
      connectedCallback() {
        this._button = this.querySelector('button');
        if (!this._button) return;
        this._handle = this.getAttribute('handle');
        this._sync();

        this._button.addEventListener('click', () => {
          const added = toggle(this._handle);
          // Saving something is the natural moment to show what is saved -
          // but only where the panel is available (see openPanel).
          if (added) openPanel();
        });

        this._onChange = () => this._sync();
        document.addEventListener('wishlist:change', this._onChange);
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
      }

      _sync() {
        this._button.setAttribute('aria-pressed', has(this._handle) ? 'true' : 'false');
      }
    }
    window.customElements.define('synergy-wish-button', SynergyWishButton);
  }

  /* ---------- Overlay panel ----------
     Desktop and tablet get the panel under the header icon. Phones follow the
     link to /wishlist instead, which is far easier to use on a small screen. */
  const PANEL_MIN_WIDTH = 700;
  // A hover-capable, fine pointer means a desktop mouse. Touch tablets report
  // (hover: none), so they keep tap-to-open rather than opening on a stray
  // touch, and phones never open the panel at all.
  const canHover = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const canPanel = () => window.matchMedia(`(min-width: ${PANEL_MIN_WIDTH}px)`).matches;
  const panelEl = () => document.querySelector('[data-syn-wish-panel]');



  /* ---------- Hover (desktop only) ---------- */
  const openerEl = () => document.querySelector('[data-syn-wish-open]');
  const openerItem = () => openerEl()?.closest('li');

  let hoverTimer = null;
  const cancelClose = () => { window.clearTimeout(hoverTimer); };
  const scheduleClose = () => {
    cancelClose();
    // Long enough to move the pointer from the icon down onto the panel.
    hoverTimer = window.setTimeout(closePanel, 260);
  };

  function bindHover() {
    const item = openerItem();
    if (!item) return;
    item.addEventListener('mouseenter', () => { if (canHover()) { cancelClose(); openPanel(); } });
    item.addEventListener('mouseleave', () => { if (canHover()) scheduleClose(); });

    const panel = panelEl();
    if (panel) {
      panel.addEventListener('mouseenter', cancelClose);
      panel.addEventListener('mouseleave', () => { if (canHover()) scheduleClose(); });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bindHover);
  else bindHover();

  /* Put the panel just under its header icon.

     "position: fixed" is not always relative to the viewport: the sticky header
     establishes a containing block, so a panel inside it is positioned against
     the header instead, and every panel landed ~60px too low. Rather than
     hard-code that offset -- it changes with the header -- place the panel, see
     where it actually landed, and correct by the difference.

     The measurement neutralises the entry transform first -- and its transition,
     or the read lands mid-animation and the 6px the panel rises through gets
     corrected away as if it were drift. */
  const PANEL_GAP = 10;

  function placePanel(panel) {
    const opener = document.querySelector('[data-syn-wish-open]');
    if (!panel || !opener) return;

    // The icon itself, not its link box, is what the panel should hang from.
    const rect = (opener.querySelector('svg') || opener).getBoundingClientRect();
    const wantTop = Math.round(rect.bottom + PANEL_GAP);
    const wantEnd = Math.max(12, Math.round(window.innerWidth - rect.right - 8));

    panel.style.top = wantTop + 'px';
    panel.style.insetInlineEnd = wantEnd + 'px';

    const transform = panel.style.transform;
    const transition = panel.style.transition;
    panel.style.transition = 'none';
    panel.style.transform = 'none';
    const got = panel.getBoundingClientRect();
    panel.style.transform = transform;
    panel.style.transition = transition;

    const driftY = Math.round(got.top - wantTop);
    const driftX = Math.round(window.innerWidth - got.right - wantEnd);
    if (driftY) panel.style.top = wantTop - driftY + 'px';
    if (driftX) panel.style.insetInlineEnd = wantEnd - driftX + 'px';
  }


  function openPanel() {
    const panel = panelEl();
    if (!panel || !canPanel()) return false;
    if (!panel.hidden) return true;          // already open; hover re-fires a lot
    panel.hidden = false;
    placePanel(panel);
    openerItem()?.classList.add('is-panel-open');
    requestAnimationFrame(() => panel.classList.add('is-open'));
    document.addEventListener('keydown', onPanelKey);
    document.addEventListener('click', onOutsideClick, true);
    return true;
  }

  function closePanel() {
    const panel = panelEl();
    if (!panel || panel.hidden) return;
    panel.classList.remove('is-open');
    openerItem()?.classList.remove('is-panel-open');
    document.removeEventListener('keydown', onPanelKey);
    document.removeEventListener('click', onOutsideClick, true);
    const done = () => { panel.hidden = true; };
    panel.addEventListener('transitionend', done, { once: true });
    window.setTimeout(done, 300);
  }

  function onPanelKey(event) {
    if (event.key === 'Escape') closePanel();
  }

  function onOutsideClick(event) {
    const panel = panelEl();
    if (!panel || panel.hidden) return;
    if (panel.contains(event.target) || event.target.closest('[data-syn-wish-open]')) return;
    closePanel();
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-syn-wish-open]');
    if (opener) {
      // Desktop: hover already shows the panel, so a click follows the link to
      // the full page. Touch tablet: no hover, so the tap toggles the panel.
      // Phone: no panel, link followed.
      if (canPanel() && !canHover()) {
        event.preventDefault();
        const panel = panelEl();
        if (panel && !panel.hidden) closePanel();
        else openPanel();
      }
      return;
    }
    if (event.target.closest('[data-syn-wish-close]')) closePanel();
  });

  // Shrinking to phone width with the panel open would leave it stranded.
  const reposition = () => {
    const panel = panelEl();
    if (panel && !panel.hidden) placePanel(panel);
  };
  window.addEventListener('resize', () => { if (!canPanel()) closePanel(); else reposition(); }, { passive: true });
  window.addEventListener('scroll', reposition, { passive: true });

  /* ---------- Header count ---------- */
  function paintCount() {
    const n = read().length;
    document.querySelectorAll('[data-syn-wish-count]').forEach((el) => {
      el.textContent = String(n);
      el.hidden = n === 0;
      // No is-filled here: a saved item shows as the count, not a filled
      // icon. Filling is reserved for hover (see the stylesheet).
    });
    document.querySelectorAll('[data-syn-wish-count-label]').forEach((el) => {
      el.textContent = n === 1 ? '1 item' : n + ' items';
    });
  }
  document.addEventListener('wishlist:change', paintCount);
  document.addEventListener('DOMContentLoaded', paintCount);
  paintCount();

  /* ---------- Item markup ---------- */
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Product titles carry the brand as a prefix so Shopify can sort collections
     by brand -- its storefront has no vendor sort. The brand already has its
     own line here, so drop the duplicate. Mirrors snippets/synergy-product-title.liquid. */
  function titleWithoutBrand(title, vendor) {
    if (!title || !vendor) return title || '';
    return title.slice(0, vendor.length) === vendor
      ? title.slice(vendor.length).trim() || title
      : title;
  }

  function optionLabel(product) {
    const first = product.options && product.options[0];
    const name = !first ? '' : (typeof first === 'string' ? first : first.name || '');
    /* Shopify calls the single default option "Title"; that is plumbing, not a
       label, so anything unnamed reads as Size. */
    return !name || /^title$/i.test(name) ? 'Size' : name;
  }

  function sizeText(product) {
    const first = product.variants && product.variants[0];
    if (product.size) return product.size;
    if (first && first.title && !/default title/i.test(first.title)) return first.title;
    return '';
  }

  /* The count and its separator belong together: with nothing saved the rule
     is "My Wish List", not "My Wish List |". Every exit from render() calls
     this, including the empty one, which is what the earlier version missed. */
  /* The button is rendered by Liquid outside the list, because its label and
     destination are section settings. Empty, it moves inside the panel; once
     there are cards, it goes back below them. */
  function adoptContinueButton(body) {
    const actions = document.querySelector('.syn-wish-page__actions');
    if (actions && body && !body.contains(actions)) body.appendChild(actions);
  }

  function releaseContinueButton(host) {
    const actions = document.querySelector('.syn-wish-page__actions');
    if (actions && host && host.parentNode && actions.parentNode !== host.parentNode) {
      host.parentNode.insertBefore(actions, host.nextSibling);
    }
  }

  function setPageCount(count) {
    const countEl = document.querySelector('[data-syn-wish-page-count]');
    if (countEl) countEl.textContent = count ? count + (count === 1 ? ' item' : ' items') : '';
    const sepEl = document.querySelector('.syn-wish-page__sep');
    if (sepEl) sepEl.hidden = !count;
  }

  /* The stepper sets how many units Move to bag will add, so the total beside
     it has to follow. Quantity is deliberately not persisted -- it describes an
     intention at the moment of moving, not a property of a saved product. */
  function adjustQty(button, delta) {
    const item = button.closest('.syn-wish__item');
    const input = item && item.querySelector('[data-syn-wish-qty]');
    if (!input) return;
    input.value = Math.max(1, (Number(input.value) || 1) + delta);
    refreshTotal(input);
  }

  function refreshTotal(input) {
    const item = input.closest('.syn-wish__item');
    const totalEl = item && item.querySelector('[data-syn-wish-total]');
    if (!totalEl) return;
    const qty = Math.max(1, Number(input.value) || 1);
    totalEl.textContent = money(Number(totalEl.getAttribute('data-unit')) * qty);
  }

  /* Choosing another size changes the price, the struck-through figure and the
     variant that Move to bag will add. */
  function syncChosenVariant(select) {
    const item = select.closest('.syn-wish__item');
    if (!item) return;
    const option = select.options[select.selectedIndex];
    const addBtn = item.querySelector('[data-syn-wish-add]');
    if (addBtn) addBtn.setAttribute('data-syn-wish-add', select.value);
    const unit = Number(option.getAttribute('data-price'));
    if (!Number.isFinite(unit)) return;
    const priceEl = item.querySelector('.syn-wish__price');
    const compareEl = item.querySelector('.syn-wish__compare');
    const compare = Number(option.getAttribute('data-compare')) || 0;
    if (priceEl) {
      priceEl.textContent = money(unit);
      priceEl.classList.toggle('syn-wish__price--sale', compare > unit);
    }
    if (compareEl) {
      compareEl.textContent = compare > unit ? money(compare) : '';
      compareEl.hidden = !(compare > unit);
    }
    const totalEl = item.querySelector('[data-syn-wish-total]');
    const qtyEl = item.querySelector('[data-syn-wish-qty]');
    if (totalEl) {
      totalEl.setAttribute('data-unit', String(unit));
      totalEl.textContent = money(unit * Math.max(1, Number(qtyEl && qtyEl.value) || 1));
    }
  }

  function cardHtml(p) {
    const available = p.variants.filter((v) => v.available);
    const chosen = available.slice().sort((a, b) => a.price - b.price)[0] || p.variants[0];
    const multi = p.variants.length > 1;
    const img = p.featured_image ? p.featured_image + '&width=220' : '';
    const onSale = chosen.compare_at_price && chosen.compare_at_price > chosen.price;

    const sizes = multi
      ? '<div class="syn-wish__size">' +
        '<label for="syn-wish-size-' + p.id + '">' + escapeHtml(optionLabel(p)) + ':</label>' +
        '<select id="syn-wish-size-' + p.id + '">' +
        p.variants
          .map(function (v) {
            return (
              '<option value="' + v.id + '"' +
              ' data-price="' + v.price + '"' +
              ' data-compare="' + (v.compare_at_price || 0) + '"' +
              (v.id === chosen.id ? ' selected' : '') +
              (v.available ? '' : ' disabled') + '>' +
              escapeHtml(v.title) + (v.available ? '' : ' - sold out') +
              '</option>'
            );
          })
          .join('') +
        '</select></div>'
      : (sizeText(p)
          ? '<span class="syn-wish__meta">' + escapeHtml(optionLabel(p)) + ': ' + escapeHtml(sizeText(p)) + '</span>'
          : '');

    return (
      '<div class="syn-wish__item" data-handle="' + escapeHtml(p.handle) + '">' +
        '<a class="syn-wish__media" href="' + p.url + '">' +
          (img ? '<img src="' + img + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
        '</a>' +
        '<div class="syn-wish__info">' +
          (p.vendor ? '<span class="syn-wish__brand">' + escapeHtml(p.vendor) + '</span>' : '') +
          '<a class="syn-wish__title" href="' + p.url + '">' + escapeHtml(titleWithoutBrand(p.title, p.vendor)) + '</a>' +
          sizes +
          (p.type ? '<span class="syn-wish__meta">' + escapeHtml(p.type) + '</span>' : '') +
          '<div class="syn-wish__actions">' +
            '<button type="button" class="syn-wish__action" data-syn-wish-add="' + chosen.id + '"' +
              (chosen.available ? '' : ' disabled') + '>' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 8h12l1 12H5L6 8Z"/><path d="M9 10V6a3 3 0 0 1 6 0v4"/></svg>' +
              'Move to bag</button>' +
            '<button type="button" class="syn-wish__action" data-syn-wish-remove="' + escapeHtml(p.handle) + '">' +
              '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M10 4h4a1 1 0 0 1 1 1v2h-6V5a1 1 0 0 1 1-1Z"/><path d="M6.5 7l.9 12.1A2 2 0 0 0 9.4 21h5.2a2 2 0 0 0 2-1.9L17.5 7"/><path d="M10 11v6M14 11v6"/></svg>' +
              'Remove</button>' +
          '</div>' +
          (chosen.available ? '' : '<span class="syn-wish__soldout">Sold out</span>') +
        '</div>' +
        /* Quantity, price and total on the foot row. The stepper is not
           decoration: it sets how many go in the bag when Move to bag is
           pressed, and the total beside it shows what that will cost. */
        '<div class="syn-wish__foot">' +
          '<div class="syn-wish__qty">' +
            '<button type="button" data-syn-wish-step="-1" aria-label="Reduce quantity">&minus;</button>' +
            '<input type="number" min="1" value="1" inputmode="numeric" data-syn-wish-qty aria-label="Quantity to move to bag">' +
            '<button type="button" data-syn-wish-step="1" aria-label="Increase quantity">+</button>' +
          '</div>' +
          '<span class="syn-wish__figure">' +
            '<span class="syn-wish__figure-label">Price:</span>' +
            '<span class="syn-wish__figure-row">' +
              '<span class="syn-wish__price' + (onSale ? ' syn-wish__price--sale' : '') + '">' + money(chosen.price) + '</span>' +
              /* Rendered either way: a size with no compare-at can be swapped
                 for one that has, and an element that was never in the DOM
                 cannot be revealed. */
              '<s class="syn-wish__compare"' + (onSale ? '' : ' hidden') + '>' +
                (onSale ? money(chosen.compare_at_price) : '') + '</s>' +
            '</span>' +
          '</span>' +
          '<span class="syn-wish__figure syn-wish__figure--total">' +
            '<span class="syn-wish__figure-label">Total:</span>' +
            '<span class="syn-wish__total" data-syn-wish-total data-unit="' + chosen.price + '">' + money(chosen.price) + '</span>' +
          '</span>' +
        '</div>' +
      '</div>'
    );
  }

  /* ---------- Drawer ---------- */
  if (!window.customElements.get('synergy-wishlist')) {
    class SynergyWishlist extends HTMLElement {
      connectedCallback() {
        this._body = this.querySelector('[data-syn-wish-body]');
        document.addEventListener('wishlist:change', () => this.render());

        this.addEventListener('click', (event) => {
          const removeBtn = event.target.closest('[data-syn-wish-remove]');
          if (removeBtn) {
            remove(removeBtn.getAttribute('data-syn-wish-remove'));
            return;
          }
          const step = event.target.closest('[data-syn-wish-step]');
          if (step) { adjustQty(step, Number(step.getAttribute('data-syn-wish-step'))); return; }
          const bagBtn = event.target.closest('[data-syn-wish-add]');
          if (bagBtn) this._moveToBag(bagBtn);
        });

        this.addEventListener('input', (event) => {
          if (event.target.closest('[data-syn-wish-qty]')) refreshTotal(event.target);
        });
        this.addEventListener('change', (event) => {
          const select = event.target.closest('.syn-wish__size select');
          if (select) syncChosenVariant(select);
        });

        this.render();
      }

      async render() {
        if (!this._body) return;
        const handles = read();

        if (!handles.length) {
          this._body.innerHTML =
            '<div class="syn-wish__empty-panel">' +
              '<p class="syn-wish__empty">' +
              (this.getAttribute('empty-text') ||
                'Your wish list is empty. Tap the heart on any product to save it here.') +
              '</p>' +
            '</div>';
          if (this.getAttribute('layout') === 'page') {
            setPageCount(0);
            /* With nothing saved, the continue button belongs inside the white
               panel with the message — the same shape the empty bag uses. It is
               moved, not duplicated, so there is only ever one of it. */
            adoptContinueButton(this._body.querySelector('.syn-wish__empty-panel'));
          }
          return;
        }

        const fetched = await Promise.all(
          handles.map(async (handle) => {
            try {
              const res = await fetch(root() + 'products/' + encodeURIComponent(handle) + '.js');
              if (!res.ok) return null;
              const product = await res.json();
              /* product.js carries no metafields, so a single-size product has
                 no size the browser can see. The wishsize view exposes just
                 that field; if it fails the card simply omits the line. */
              try {
                const sz = await fetch(root() + 'products/' + encodeURIComponent(handle) + '?view=wishsize');
                if (sz.ok) product.size = (await sz.text()).trim();
              } catch (e) { /* size is optional */ }
              return product;
            } catch (e) {
              return null;
            }
          })
        );

        // A saved product that no longer resolves (unpublished, renamed) is
        // dropped rather than rendered broken.
        const products = fetched.filter(Boolean);
        if (products.length !== handles.length) {
          write(products.map((p) => p.handle));
          return;
        }

        const grid = this.getAttribute('layout') === 'page';   // panel uses the list
        this._body.innerHTML =
          '<div class="' + (grid ? 'syn-wish__grid' : 'syn-wish__list') + '">' +
          products.map(cardHtml).join('') + '</div>';
        if (this.getAttribute('layout') === 'page') {
          setPageCount(products.length);
          releaseContinueButton(this);
        }
      }

      async _moveToBag(button) {
        const row = button.closest('.syn-wish__item');
        const qtyInput = row && row.querySelector('[data-syn-wish-qty]');
        const qty = Math.max(1, Number(qtyInput && qtyInput.value) || 1);
        const item = button.closest('.syn-wish__item');
        const select = item.querySelector('select');
        const id = select ? select.value : button.getAttribute('data-syn-wish-add');
        button.setAttribute('disabled', '');

        try {
          const res = await fetch(root() + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(id), quantity: qty }] }),
          });
          if (!res.ok) throw new Error('add to cart failed');

          const cart = await (await fetch(root() + 'cart.js')).json();

          // The same event the theme's own product form fires, so cart counts
          // refresh and the cart drawer opens exactly as it does elsewhere.
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart: cart },
            })
          );

          remove(item.getAttribute('data-handle'));
          closePanel();   // the cart drawer is about to take over the screen
        } catch (e) {
          button.removeAttribute('disabled');
        }
      }
    }
    window.customElements.define('synergy-wishlist', SynergyWishlist);
  }
})();
